import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { readMenuAll } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, Item, ProductHierarchyTag } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';

import { useProductInformationSettings } from './useProductInformationSettings';
import { getItemCategory, validateItemPurchasability, WOLT_SPECIALS_CATEGORY_ID } from '../utils/menuItemsUtils';

export type DiscountedItem = Omit<Item, 'name' | 'description'> & {
  name: string;
  description: string;
  category?: { id: string; name: string };
};

const BABY_FORMULA_FAMILY_ID = '007001004';
const SMOKERS_ITEMS_FAMILY_ID = '006005005';
const NEWSPAPER_FAMILY_ID = '011004001';
const MAGAZINES_FAMILY_ID = '011004002';
const LOTTERY_SCRATCHCARDS_FAMILY_ID = '011004003';

export const useDiscountedItems = (menuId: string) => {
  const { locale } = useIntl();

  const {
    productHierarchyLevelsMap,
    isFetching: isLoadingProductInformationSetting,
    errorProductInformationSettings,
  } = useProductInformationSettings(menuId);

  const { alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();

  const { data, isFetching, error } = useQuery(['read-menu-all', menuId], () => readMenuAll(menuId), {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const hasProductHierarchyTag = (item: Item, type: ProductHierarchyTag) => {
    if (item.product_hierarchy_level_id && productHierarchyLevelsMap.has(item.product_hierarchy_level_id)) {
      const productHierarchyLevel = productHierarchyLevelsMap.get(item.product_hierarchy_level_id);
      return productHierarchyLevel?.tags.includes(type);
    }
    return false;
  };

  const hasHierarchyLevel = (item: Item, humanReadableIds: string[]) => {
    if (item.product_hierarchy_level_id && productHierarchyLevelsMap.has(item.product_hierarchy_level_id)) {
      const productHierarchyLevel = productHierarchyLevelsMap.get(item.product_hierarchy_level_id);
      return (
        productHierarchyLevel?.human_readable_id && humanReadableIds.includes(productHierarchyLevel.human_readable_id)
      );
    }
    return false;
  };

  const isItemIncludedInDiscount = (item: Item, itemCategory: Category | undefined) => {
    const isAlcohol =
      hasProductHierarchyTag(item, 'alcohol') ||
      (alcoholWarningPermilleLimit !== undefined && item.alcohol_percentage > alcoholWarningPermilleLimit);
    const isOverTheCounter = hasProductHierarchyTag(item, 'over_the_counter');
    const hasRestrictions = item.restrictions.length > 0; // this also includes over the counter items, in case of removal update isOverTheCounter
    const isWoltSpecials = itemCategory?.id === WOLT_SPECIALS_CATEGORY_ID;
    const isItemPurchasable = data?.options && validateItemPurchasability(data?.options, item).length === 0;

    return (
      !isAlcohol &&
      !hasRestrictions &&
      !isOverTheCounter &&
      !hasHierarchyLevel(item, [
        BABY_FORMULA_FAMILY_ID,
        LOTTERY_SCRATCHCARDS_FAMILY_ID,
        MAGAZINES_FAMILY_ID,
        NEWSPAPER_FAMILY_ID,
        SMOKERS_ITEMS_FAMILY_ID,
      ]) &&
      item.enabled &&
      !!itemCategory &&
      !isWoltSpecials &&
      !!isItemPurchasable
    );
  };

  const selectableItems =
    data?.items.reduce<DiscountedItem[]>((items, item) => {
      const itemCategory = getItemCategory(data.menu.categories, item.id);
      if (itemCategory && isItemIncludedInDiscount(item, itemCategory)) {
        items.push({
          ...item,
          name: localize(item.name, locale),
          description: localize(item.description, locale),
          category: { id: itemCategory.id, name: localize(itemCategory.name, locale) },
        });
      }
      return items;
    }, []) ?? [];

  // Sort the items so that they are in the order of the categories
  const sortedItems: DiscountedItem[] =
    data?.menu.categories.flatMap(category => selectableItems.filter(item => item.category?.id === category.id)) ?? [];

  return {
    discountedItems: sortedItems,
    isLoadingMenuData: isFetching || isLoadingProductInformationSetting,
    hasErrorMenuData: error || errorProductInformationSettings,
  };
};
